import {
  extractTrailingSearchFilter,
  hasFilterBindingKeyword,
  isValidEmail,
  SearchFilterKeyword,
  stripStringOfPotentialFilterBindings,
  toFilterBinding,
  typeToKeyword,
  withDelimiter,
} from "@mirage/shared/search/search-filters";

import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import type { SearchFilterType } from "@mirage/shared/search/search-filters";

export function predicateFactory<T = typeahead.SectionId | typeahead.SourceId>(
  query: string,
  mode: typeahead.Mode,
  subjects: typeahead.PredicateSubject<T>[],
  options: typeahead.PredicateFunctionOptions,
): typeahead.PredicateSubject<T>[] {
  return subjects.filter((subject) => subject.predicate(query, mode, options));
}

/**
 * Predicate Combiners
 */

export function and(
  ...predicates: Array<typeahead.PredicateFunction>
): typeahead.PredicateFunction {
  return function predicate(...args) {
    return predicates.every((predicate) => predicate(...args));
  };
}

export function or(
  ...predicates: Array<typeahead.PredicateFunction>
): typeahead.PredicateFunction {
  return function predicate(...args) {
    return predicates.some((predicate) => predicate(...args));
  };
}

export function minimumQueryLength(
  length: number,
): typeahead.PredicateFunction {
  return function predicate(query) {
    return query.length >= length;
  };
}

export function exactQueryLength(length: number): typeahead.PredicateFunction {
  return function predicate(query) {
    return query.length === length;
  };
}

export function minStrippedQueryLength(
  length: number,
): typeahead.PredicateFunction {
  return function predicate(query) {
    return stripStringOfPotentialFilterBindings(query).trim().length >= length;
  };
}

export function isDropboxer(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return options.config.isDropboxer;
  };
}

export function isURLShortcut(): typeahead.PredicateFunction {
  return function predicate(query) {
    return query.startsWith("/");
  };
}

export function isNotURLShortcut(): typeahead.PredicateFunction {
  return function predicate(query) {
    return !query.startsWith("/");
  };
}

export function nullStateRecommendationsEnabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return options.config.isNullStateUsesRecommendations;
  };
}

export function nullStateRecommendationsDisabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return !options.config.isNullStateUsesRecommendations;
  };
}

export function isRecentQueriesEnabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return options.config.isRecentQueriesEnabled;
  };
}

export function isRecentQueriesDisabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return !options.config.isRecentQueriesEnabled;
  };
}

export function isTypeaheadFiltersEnabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return options.config.isTypeaheadFiltersEnabled;
  };
}

export function doesNotContainIncompleteTrailingFilterBinding(
  allowBypassIncompletePeopleFilter = false,
): typeahead.PredicateFunction {
  return function predicate(...args) {
    return !containsIncompleteTrailingFilterBinding(
      allowBypassIncompletePeopleFilter,
    )(...args);
  };
}

export function containsIncompleteTrailingFilterBinding(
  allowBypassIncompletePeopleFilter = false,
): typeahead.PredicateFunction {
  return function predicate(query, _mode, { config: { allFiltersSet } }) {
    const { filterType, query: filterText } =
      extractTrailingSearchFilter(query);

    if (!filterType) return false;

    const filterKeyword = typeToKeyword(filterType);

    if (!filterKeyword) return false;

    const filterBindingStr = toFilterBinding(filterKeyword, filterText);

    if (allFiltersSet.has(filterBindingStr)) {
      return false;
    }

    if (
      allowBypassIncompletePeopleFilter &&
      filterKeyword === SearchFilterKeyword.Person &&
      isValidEmail(filterText) &&
      !Array.from(allFiltersSet).some((filter) => {
        const filterTextPart = filter.split(":")?.[1];
        if (!filterTextPart) return false;
        return filterTextPart
          .toLowerCase()
          .startsWith(filterText.toLowerCase());
      })
    ) {
      return false;
    }

    return true;
  };
}

export function doesNotContainFilterBindingPrefix(): typeahead.PredicateFunction {
  return function predicate(query) {
    return !hasFilterBindingKeyword(query);
  };
}

export function hasRecentQueries(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return options.cacheAnalysis.hasRecentQueries;
  };
}

export function doesntHaveRecentQueries(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return !options.cacheAnalysis.hasRecentQueries;
  };
}

export function doesNotIntroduceMultiSelectFor(
  type: SearchFilterType,
): typeahead.PredicateFunction {
  return function predicate(query) {
    const filterKeyword = typeToKeyword(type);
    if (!filterKeyword) return true;
    return !(
      query
        .split(" ")
        .filter((part) => part.includes(withDelimiter(filterKeyword))).length >
        1 && extractTrailingSearchFilter(query).filterType === type
    );
  };
}

export function isModeIn(
  ...modes: typeahead.Mode[]
): typeahead.PredicateFunction {
  return function predicate(_query, mode) {
    return modes.includes(mode);
  };
}

export function hasAttachmentUploadFilenames(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return !!options.config.attachmentUploadFilenames?.length;
  };
}

export function isOmniboxEnabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return options.config.isOmniboxEnabled;
  };
}

export function isOmniboxDisabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return !options.config.isOmniboxEnabled;
  };
}

export function isPeopleFilterRowEnabled(): typeahead.PredicateFunction {
  return function predicate(_query, _mode, options) {
    return options.config.isPeopleFilterRowEnabled;
  };
}
